// The journal screen on @rpg/ui (decision 0046): two tabs, active (the quests the player accepted) and finished (done
// and failed), each quest's title, kind, giver, tracked and failed marks, summary and objectives (done or current, with
// a count where it takes several), and a button to track an active quest that isn't. Cut from spike 002's
// src/story/ui.ts; every text is a string id the game passes (`labels`). Its styles are `JOURNAL_CSS`.
// A quest only offered (not yet accepted) isn't listed: a game whose rule is never to push quests can use the journal
// as it is (IMP-0032). `showOffered` brings back 0.1.0's Active, offered quests beside the accepted ones. Planted
// defect `QUEST_JOURNAL_FAULTS.offeredInActive` lists them whatever the knob says.
import { Button, h, Screen, Status, TabPanel, Tabs, type ScreenContext, type ScreenRender } from "@rpg/ui";
import type { JournalEntry } from "@rpg/story";
import { QUEST_JOURNAL_FAULTS } from "./faults";

/** The string ids the journal and its notices say things with (defaults in `QUEST_JOURNAL_STRINGS`). */
export interface JournalLabels {
  title: string;
  /** The pause menu's entry. */
  open: string;
  tabs: string;
  active: string;
  done: string;
  none: string;
  main: string;
  side: string;
  /** `{name}`. */
  giver: string;
  tracked: string;
  failed: string;
  /** `{title}`. */
  track: string;
  /** `{label}`. */
  objectiveDone: string;
  objectiveCurrent: string;
  /** `{label}`, `{count}`, `{need}`. */
  count: string;
  /** Notices: `{label}` an objective done; `{title}` a quest taken, done, failed. */
  noticeObjective: string;
  noticeQuest: string;
  noticeDone: string;
  noticeFailed: string;
}

/** The unit's default labels (its own string ids). */
export const JOURNAL_LABELS: JournalLabels = {
  title: "journal.title",
  open: "journal.open",
  tabs: "journal.tabs",
  active: "journal.active",
  done: "journal.done",
  none: "journal.none",
  main: "journal.main",
  side: "journal.side",
  giver: "journal.giver",
  tracked: "journal.tracked",
  failed: "journal.failed",
  track: "journal.track",
  objectiveDone: "journal.objective.done",
  objectiveCurrent: "journal.objective.current",
  count: "journal.count",
  noticeObjective: "journal.notice.objective",
  noticeQuest: "journal.notice.quest",
  noticeDone: "journal.notice.done",
  noticeFailed: "journal.notice.failed",
};

/** The journal screen's and the notice's styles (add once to the page). */
export const JOURNAL_CSS = `
.rpg-screen[data-screen=quests] .rpg-quest{margin:.3em 0 .9em}
.rpg-screen[data-screen=quests] .rpg-quest h2{font-size:1.05em;margin:.2em 0}
.rpg-screen[data-screen=quests] .rpg-quest ul{margin:.2em 0;padding-left:1.2em}
.rpg-screen[data-screen=quests] .rpg-quest p{margin:.2em 0}
.rpg-screen[data-screen=quests] .rpg-quest-meta span+span::before{content:" · "}
.rpg-quest-notice{position:fixed;left:50%;top:13%;transform:translateX(-50%);z-index:13;display:none;padding:9px 16px;border-radius:10px;
  background:rgba(29,42,31,.88);color:#fbf6e8;font:600 16px/1.3 system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.25);pointer-events:none;text-align:center}
.rpg-quest-notice[data-shown]{display:block}
`;

/** What the journal reads and does. */
export interface JournalScreenHost {
  /** The quests in the log now (@rpg/story's `journal()`). */
  journal(): readonly JournalEntry[];
  /** Track a quest (its objective becomes the marker's). */
  track(quest: string): void;
  /** A giver's name (a string id). */
  npcName(npc: string): string;
  t(id: string, vars?: Record<string, string | number>): string;
  labels?: JournalLabels;
  /** List quests only offered (not yet accepted) in Active beside the accepted ones, as 0.1.0 did (default false:
   *  Active lists only the quests the player accepted). */
  showOffered?: boolean;
}

/** The journal's tabs. */
export type JournalTab = "active" | "done";

/** The entries a tab lists, in the journal's order: Active the accepted quests (and the offered ones with
 *  `showOffered`), finished the done and failed ones. Pure; the screen uses it, and so can a game's own screen. */
export function journalTabEntries(all: readonly JournalEntry[], tab: JournalTab, showOffered = false): JournalEntry[] {
  const offered = showOffered || QUEST_JOURNAL_FAULTS.offeredInActive;
  return all.filter((j) => (tab === "active" ? j.status === "active" || (offered && j.status === "offered") : j.status === "done" || j.status === "failed"));
}

/** The journal screen (register it as `quests`: its styles key on `data-screen=quests`). */
export function journalScreen(o: JournalScreenHost): ScreenRender {
  const L = o.labels ?? JOURNAL_LABELS;
  const t = o.t;
  let tab: JournalTab = "active";
  return (ctx: ScreenContext) => {
    const all = o.journal();
    const shown = journalTabEntries(all, tab, o.showOffered === true);
    const entry = (j: JournalEntry) =>
      h(
        "section",
        { class: "rpg-quest", "data-quest": j.quest },
        h("h2", null, t(j.title)),
        // each part its own text from the strings table (a raw-string scan reads text nodes)
        h(
          "p",
          { class: "rpg-quest-meta" },
          ...[t(j.kind === "main" ? L.main : L.side), j.giver ? t(L.giver, { name: t(o.npcName(j.giver)) }) : null, j.tracked ? t(L.tracked) : null, j.status === "failed" ? t(L.failed) : null]
            .filter((x): x is string => x !== null)
            .map((x) => h("span", null, x)),
        ),
        j.summary ? h("p", null, t(j.summary)) : null,
        h(
          "ul",
          null,
          ...j.objectives.map((ob) => {
            const label = ob.need > 1 && !ob.done ? t(L.count, { label: t(ob.label), count: ob.count, need: ob.need }) : t(ob.label);
            return h("li", { "data-objective": ob.id, "data-done": ob.done ? "" : undefined }, t(ob.done ? L.objectiveDone : L.objectiveCurrent, { label }));
          }),
        ),
        j.status === "active" && !j.tracked ? h(Button, { label: t(L.track, { title: t(j.title) }), action: `track-${j.quest}`, onPress: () => (o.track(j.quest), ctx.ui.refresh()) }) : null,
      );
    return h(
      Screen,
      { ctx, title: t(L.title), dim: true },
      h(Tabs, {
        id: "quest-tabs",
        label: t(L.tabs),
        tabs: [
          { id: "active", label: t(L.active) },
          { id: "done", label: t(L.done) },
        ],
        selected: tab,
        onSelect: (id: string) => {
          tab = id === "done" ? "done" : "active";
          ctx.ui.refresh();
        },
      }),
      h(TabPanel, { id: "quest-tabs", selected: tab }, ...(shown.length ? shown.map(entry) : [h(Status, { text: t(L.none) })])),
    );
  };
}
